<template>
  <div id="coach-home-dashboard">
    <trainee-view-sidebar :isSidebarActive="addNewDataSidebar" @closeSidebar="toggleDataSidebar" />
    
    <!-- COACH ROSTER OVERVIEW -->
    <div class="vx-row mb-6 mt-4">
      <div class="vx-col w-full sm:w-1/2 md:w-1/4 mb-base">
        <vx-card class="text-center">
          <h2 class="text-4xl font-bold text-primary">{{ assignedFounders.length }}</h2>
          <p class="text-gray-500 mt-2 font-medium">Assigned Founders</p>
        </vx-card>
      </div>
      <div class="vx-col w-full sm:w-1/2 md:w-1/4 mb-base">
        <vx-card class="text-center">
          <h2 class="text-4xl font-bold text-success">1</h2> 
          <p class="text-gray-500 mt-2 font-medium">Active This Week</p>
        </vx-card>
      </div>
      <div class="vx-col w-full sm:w-1/2 md:w-1/4 mb-base">
        <vx-card class="text-center">
          <h2 class="text-4xl font-bold text-warning">{{ dynamicPendingEvidence.length }}</h2>
          <p class="text-gray-500 mt-2 font-medium">Pending Reviews</p>
        </vx-card>
      </div>
      <div class="vx-col w-full sm:w-1/2 md:w-1/4 mb-base">
        <vx-card class="text-center">
          <h2 class="text-4xl font-bold text-danger">0</h2>
          <p class="text-gray-500 mt-2 font-medium">Needs Attention</p>
        </vx-card>
      </div>
    </div>

    <!-- FOUNDER SELECTOR -->
    <div v-if="assignedFounders.length > 1">
      <h3 class="mb-4 text-lg font-semibold">Select Founder to View Progress</h3>
      <div class="vx-row flex-nowrap overflow-x-auto pb-4 mb-6">
        <div 
          class="vx-col cursor-pointer min-w-[250px]" 
          v-for="(founder, index) in assignedFounders" 
          :key="index"
          @click="selectFounder(founder)"
        >
          <vx-card 
            class="transition-all duration-200"
            :class="{'border-2 border-primary shadow-md': selectedFounder && selectedFounder.id === founder.id}">
            <div class="flex items-center">
              <vs-avatar size="large" :text="founder.userData[0].firstName" class="mr-4" />
              <div>
                <h5 class="font-bold">{{ founder.userData[0].firstName }} {{ founder.userData[0].lastName }}</h5>
                <div class="flex items-center mt-1">
                  <div class="h-2 w-2 rounded-full mr-2 bg-success"></div>
                  <span class="text-sm text-gray-500">Active</span>
                </div>
              </div>
            </div>
          </vx-card>
        </div>
      </div>
    </div>

    <!-- SELECTED FOUNDER DETAILS -->
    <transition name="fade">
      <div v-if="selectedFounder" class="selected-founder-details">
        
        <!-- Action Header -->
        <div class="flex flex-wrap items-center justify-between mb-4 bg-white p-4 rounded-lg shadow-sm">
          <div class="flex items-center">
            <h2 class="text-2xl font-bold mr-4">{{ selectedFounder.userData[0].firstName }}'s Progress</h2>
            <vs-chip color="primary" transparent>Joined: {{ formatDate(selectedFounder.createdAt) }}</vs-chip>
          </div>
          <div class="flex gap-2 items-center">
            <vs-button icon-pack="feather" icon="icon-calendar" color="warning" type="border" @click="scheduleMeeting(selectedFounder.userData[0].id)">Schedule Meeting</vs-button>
            <div class="relative flex items-center justify-center">
              <vs-button icon-pack="feather" icon="icon-file-text" color="primary" type="border" @click.stop.prevent="triggerNotes">Coach Notes</vs-button>
              <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; pointer-events: none; z-index: -1;">
                <trainee-coach-notes v-if="selectedFounder" ref="coachNotesRef" :commuId="selectedFounder.userData[0].id" :key="'note-'+selectedFounder.userData[0].id"></trainee-coach-notes>
              </div>
            </div>
            <vs-button icon-pack="feather" icon="icon-list" color="success" type="filled" @click="editData(selectedFounder.userData[0].id)">Manage Skills</vs-button>
          </div>
        </div>

        <!-- ================= 3 IN ONE LINE: PROGRESS CHARTS ================= -->
        <!-- Added flex and stretch classes to make sure all 3 boxes are EXACTLY the same height -->
        <div class="vx-row flex items-stretch">
        
        <!-- 1. OVERALL PROGRESS CHART (Left) -->
        <div class="vx-col w-full lg:w-1/3 mb-base flex">
            <vx-card title="Overall Framework" class="w-full h-full flex flex-col justify-between">
            <div class="flex-grow flex items-center justify-center">
                <vue-apex-charts type="radialBar" height="260" :options="radialChartOptions" :series="[currentProgress]"></vue-apex-charts>
            </div>
            <div class="text-center mt-2">
                <p class="text-sm text-gray-500">Percentage of total skills completed.</p>
            </div>
            </vx-card>
        </div>

        <!-- 2. CURRENT STAGE TOTALS (Center) -->
        <div class="vx-col w-full lg:w-1/3 mb-base flex">
            <vx-card title="Total Skills Pipeline" class="w-full h-full flex flex-col justify-between">
            <div class="flex-grow flex flex-col items-center justify-center">
                <vue-apex-charts type="donut" height="210" :options="donutChartOptions" :series="[skillsMastered, skillsWorkingOn, skillsAwaitingReview]"></vue-apex-charts>
                
                <ul class="w-full mt-4 px-2">
                    <li class="flex justify-between items-center mb-2">
                        <span class="flex items-center text-sm"><span class="h-3 w-3 rounded-full bg-success mr-2"></span> Skills Mastered</span>
                        <span class="font-bold text-sm">{{ skillsMastered }}</span>
                    </li>
                    <li class="flex justify-between items-center mb-2">
                        <span class="flex items-center text-sm"><span class="h-3 w-3 rounded-full bg-warning mr-2"></span> Working On It</span>
                        <span class="font-bold text-sm">{{ skillsWorkingOn }}</span>
                    </li>
                    <li class="flex justify-between items-center">
                        <span class="flex items-center text-sm"><span class="h-3 w-3 rounded-full mr-2" style="background-color: #7367F0;"></span> Awaiting Review</span>
                        <span class="font-bold text-sm">{{ skillsAwaitingReview }}</span>
                    </li>
                </ul>
            </div>
            </vx-card>
        </div>

        <!-- 3. INDIVIDUAL SKILL PULSE (Right) -->
        <div class="vx-col w-full lg:w-1/3 mb-base flex">
            <vx-card title="Individual Skill Pulse" class="w-full h-full flex flex-col justify-between">
            
            <!-- Dropdown -->
<div
  class="w-full mb-6"
  style="position: relative; top: -50px;"
>
  <vs-select
    autocomplete
    class="w-full"
    v-model="selectedSkillId"
    placeholder="Search / Select Skill..."
  >
    <vs-select-item
      v-for="skill in founderSkills"
      :key="skill.id"
      :value="skill.id"
      :text="skill.skillName"
    />
  </vs-select>
</div>

            <!-- Replace your existing v-if="selectedSkillId" div with this: -->
            <div v-if="selectedSkillId" class="flex-grow flex justify-between items-center w-full" style="position: relative; top: -40px;">
                
                <!-- Relevancy (Green) -->
                <div class="flex flex-col items-center w-1/3">
                  <vue-apex-charts type="radialBar" height="150" :options="relevancyOptions" :series="[skillPulseData.relevancy]"></vue-apex-charts>
                  <span class="text-xs font-bold text-center mt-1 text-gray-700">Relevancy</span>
                </div>
                <!-- Done (Purple) -->
                <div class="flex flex-col items-center w-1/3 border-l border-r border-gray-100">
                  <vue-apex-charts type="radialBar" height="150" :options="doneOptions" :series="[skillPulseData.done]"></vue-apex-charts>
                  <span class="text-xs font-bold text-center mt-1 text-gray-700">Done</span>
                </div>
                <!-- Doing (Orange) -->
                <div class="flex flex-col items-center w-1/3">
                  <vue-apex-charts type="radialBar" height="150" :options="doingOptions" :series="[skillPulseData.doing]"></vue-apex-charts>
                  <span class="text-xs font-bold text-center mt-1 text-gray-700">Doing</span>
                </div>

            </div>
            
            <!-- You can also optionally move the "empty state" message up to match -->
            <div v-else class="flex-grow flex flex-col items-center justify-center text-gray-400 py-6" style="position: relative; top: -40px;">
                <feather-icon icon="MousePointerIcon" class="w-8 h-8 mb-2 opacity-50" />
                <p class="text-sm">Select a skill above to view details.</p>
            </div>

            </vx-card>
        </div>

        </div>

        <!-- EVIDENCE PENDING REVIEW TABLE -->
        <div class="vx-row">
          <div class="vx-col w-full">
            <vx-card title="Evidence Pending Review">
               <vs-table :data="dynamicPendingEvidence" class="mt-2">
                <template slot="thead">
                  <vs-th>Skill / Competency</vs-th>
                  <vs-th>Submission Date</vs-th>
                  <vs-th>Evidence Type</vs-th>
                  <vs-th>Action</vs-th>
                </template>
                <template slot-scope="{data}">
                  <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                    <vs-td class="font-medium">{{ tr.skillName }}</vs-td>
                    <vs-td>{{ tr.date }}</vs-td>
                    <vs-td>
                      <vs-chip color="warning" transparent>
                        <feather-icon icon="FileTextIcon" svgClasses="w-3 h-3 mr-1"></feather-icon>
                        {{ tr.type }}
                      </vs-chip>
                    </vs-td>
                    <vs-td>
                      <vs-button size="small" color="success" icon-pack="feather" icon="icon-check-circle" @click="editData(selectedFounder.userData[0].id)">Review Now</vs-button>
                    </vs-td>
                  </vs-tr>
                  
                  <vs-tr v-if="data.length === 0">
                    <vs-td colspan="4" class="text-center py-6 text-gray-500">
                      <p>No evidence pending review for this founder.</p>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
            </vx-card>
          </div>
        </div>

      </div>
    </transition>
    
    <!-- ================= CALENDAR & MEETING POPUPS ================= -->
    <vs-popup class="custom-calendar-popup" title="Schedules" :active.sync="calenderPopup">
         <div id="simple-calendar-app">
        <div class="no-scroll-content">
          <calendar-view
              ref="calendar"
              :displayPeriodUom="calendarView"
              :show-date="showDate"
              :events="eventsData"
              enableDragDrop
              timeFormat="h:mm a"
              eventTop="3rem"
              eventBorderHeight="2px"
              eventContentHeight="1.85rem"
              class="theme-default custom-calendar-view"
              style="padding:10px"
              @click-date="promptAddMeeting"
              @click-day="promptAddMeeting">

            <div slot="header" class="mb-4">
              <div class="vx-row no-gutter">
                <div class="vx-col w-1/3 items-center sm:flex hidden">
                  <template v-for="(view, index) in calendarViewTypes">
                    <vs-button v-if="calendarView === view.val" :key="String(view.val) + 'filled'" type="filled" class="p-3 md:px-8 md:py-3" :class="{'border-l-0 rounded-l-none': index, 'rounded-r-none': calendarViewTypes.length !== index+1}" @click="calendarView = view.val">{{ view.label }}</vs-button>
                    <vs-button v-else :key="String(view.val) + 'border'" type="border" class="p-3 md:px-8 md:py-3" :class="{'border-l-0 rounded-l-none': index, 'rounded-r-none': calendarViewTypes.length !== index+1}" @click="calendarView = view.val">{{ view.label }}</vs-button>
                  </template>
                </div>

                <div class="vx-col sm:w-1/3 w-full flex justify-center mt-3 sm:mt-0">
                  <feather-icon icon="CircleIcon" title='Skill not added' :svgClasses="['fill-current h-4 w-4', 'text-danger']" />
                  <feather-icon icon="CircleIcon" title='Start working on' :svgClasses="['fill-current h-4 w-4', 'text-primary']" class="ml-4"/>
                  <feather-icon icon="CircleIcon" title='Goal Achieved' :svgClasses="['fill-current h-4 w-4', 'text-success']" class="ml-4"/>
                  <feather-icon icon="CircleIcon" title='Meeting Scheduled' :svgClasses="['fill-current h-4 w-4', 'text-warning']" class="ml-4"/>
                </div>

                <div class="vx-col sm:w-1/3 w-full sm:my-0 my-3 flex sm:justify-end justify-center">
                  <div class="flex items-center">
                    <feather-icon icon="ChevronLeftIcon" @click="updateMonth(-1)" svgClasses="w-5 h-5 m-1" class="cursor-pointer bg-primary text-white rounded-full" />
                    <span class="mx-3 text-xl font-medium whitespace-no-wrap">
                    {{ calendarView === 'year' ? showDate.getFullYear() : showDate.toLocaleString('default', { month: 'long', year: 'numeric' }) }}
                    </span>
                    <feather-icon icon="ChevronRightIcon" @click="updateMonth(1)" svgClasses="w-5 h-5 m-1" class="cursor-pointer bg-primary text-white rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </calendar-view>
        </div>
      </div>
    </vs-popup>

    <!-- SCHEDULE MEETING POPUP -->
    <vs-popup class="calendar-event-dialog meeting-popup" :title="'Schedule Meeting with ' + currentTraineeName" :active.sync="activePromptAddMeeting" style="z-index: 100000;">
      <div class="relative mt-1 p-2">
        <div class="flex justify-between my-2">
          <label class="mt-2 mr-1 font-semibold">Start Time</label>
          <div class="flex w-full gap-2">
            <vs-input v-model.number="startH" type="number" color="warning" :min="0" :max="23" placeholder="HH" class="w-1/2" />
            <vs-input v-model.number="startM" type="number" color="warning" :min="0" :max="59" placeholder="MM" class="w-1/2" />
          </div>
        </div>
        <div class="flex justify-between my-2">
          <label class="mt-2 mr-1 font-semibold">Time Zone</label>
          <select class="w-full p-2 border rounded text-primary" v-model="timezone">
            <option value="Asia/Kolkata">Asia/Calcutta</option>
            <option value="GMT">GMT</option>
            <option value="America/New_York">America/New_York</option>
          </select>
        </div>
        <div class="flex justify-between my-2 items-center">
          <label class="mt-2 mr-1 font-semibold text-sm">Use My Link?</label>
          <vs-switch v-model="useMyMeetingLink" color="success" />
        </div>
        <div v-if="!useMyMeetingLink" class="flex justify-between my-2">
          <label class="mt-2 mr-1 font-semibold">Meeting Link</label>
          <vs-input v-model="meetingUrl" class="w-full text-primary" placeholder="Optional URL" />
        </div>
        <div class="relative mt-4">
          <vs-textarea v-model="msg" class="w-full resize-none" placeholder="Meeting Purpose" rows="3" />
        </div>
        <div class="flex justify-center mt-4">
          <vs-button class="w-full" color="warning" type="filled" @click="createMeeting" :disabled="msg === '' || msg === null">Save Meeting</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import TraineeViewSidebar from '@/views/user/my-coach/trainee-list/TraineeViewSidebar.vue'
import TraineeCoachNotes from '@/layouts/components/navbar/components/Notes.vue'

import { CalendarView, CalendarViewHeader } from 'vue-simple-calendar'
import Datepicker from 'vuejs-datepicker'
import moduleCalendar from '@/store/calendar/moduleCalendar.js'

// Import ALL Framework Data for accurate name resolving!
import baseData from "@/i18n/i18n-en-Data-DPM.js";           
import deepData from "@/i18n/i18n-en-deep-Data.js";       
import pciDataCoach from "@/i18n/i18n-en-pci-Data-Coach.js";
import pciData from "@/i18n/i18n-en-pci-Data.js";
import peopletreeData from "@/i18n/i18n-en-peopletree-Data.js"; 
import standardData from "@/i18n/i18n-en-Data.js";
require('vue-simple-calendar/static/css/default.css')

export default {
  components: {
    VueApexCharts, TraineeViewSidebar, TraineeCoachNotes, CalendarView, CalendarViewHeader, Datepicker
  },
  data() {
    return {
      assignedFounders: [],
      selectedFounder: null,
      currentProgress: 0,
      skillsMastered: 0,
      skillsWorkingOn: 0,
      skillsAwaitingReview: 0,
      dynamicPendingEvidence: [],
      
      // Individual Skill Tracking
      founderSkills: [],
      selectedSkillId: null,
      skillPulseData: { relevancy: 0, done: 0, doing: 0 }, 

      calenderPopup: false,
      showDate: new Date(),
      eventsData: [],
      calendarViewTypes: [{ label: 'Week', val: 'week' }, { label: 'Month', val: 'month' }, { label: 'Year', val: 'year' }],
      calendarView: 'month',
      
      // Meeting Popup State
      activePromptAddMeeting: false,
      currentTraineeId: null,
      currentTraineeName: '',
      meetingDate: new Date(),
      startH: 0, startM: 0, timezone: 'GMT', useMyMeetingLink: false, myMeetingLink: null, meetingUrl: '', msg: '',
      addNewDataSidebar: false, sidebarData: {},

      // ---- APEX CHART OPTIONS ----
      radialChartOptions: {
        chart: { type: 'radialBar' },
        plotOptions: {
          radialBar: { hollow: { size: '65%' }, dataLabels: { name: { show: true, fontSize: '16px', color: '#6e6b7b' }, value: { show: true, fontSize: '30px', fontWeight: 'bold', color: '#5e5873' } } }
        },
        colors: ['#7367F0'], labels: ['Completed'],
      },

      donutChartOptions: {
        chart: { type: 'donut' },
        labels: ['Skills Mastered', 'Working On It', 'Awaiting Review'],
        colors: ['#28C76F', '#FF9F43', '#7367F0'], 
        dataLabels: { enabled: false }, legend: { show: false }, 
        plotOptions: {
          pie: { donut: { size: '75%', labels: { show: true, name: { show: true, fontSize: '12px' }, value: { show: true, fontWeight: 'bold', fontSize: '24px' }, total: { show: true, label: 'Total Skills', color: '#6e6b7b', fontSize: '14px' } } } }
        }
      },

      // INDIVIDUAL 3-CHART OPTIONS
      relevancyOptions: this.getMiniRadialConfig('#28C76F'),
      doneOptions: this.getMiniRadialConfig('#7367F0'),
      doingOptions: this.getMiniRadialConfig('#FF9F43')
    }
  },
  watch: {
    selectedSkillId(newId) {
      if(newId) {
        this.fetchSkillProgress(newId);
      }
    }
  },
  created() {
    this.fetchFounders();
    if (!this.$store.hasModule('calendar')) this.$store.registerModule('calendar', moduleCalendar);
    this.$store.dispatch("userManagement/fetchUser", this.$store.state.AppActiveUser.usersId)
      .then(res => { this.myMeetingLink = res.meetingLink || res.meetingUrl || null; }).catch(err => console.error(err));
  },
  beforeDestroy() {
    this.$store.unregisterModule('calendar');
  },
  methods: {
    // Generate mini chart configs
    getMiniRadialConfig(colorHex) {
      return {
        chart: { 
          type: 'radialBar',
          animations: { enabled: true, easing: 'easeinout', speed: 600, animateGradually: { enabled: true, delay: 50 }, dynamicAnimation: { enabled: true, speed: 350 } }
        },
        plotOptions: {
          radialBar: {
            hollow: { size: '55%' },
            dataLabels: {
              show: true, name: { show: false }, 
              value: { show: true, fontSize: '16px', fontWeight: 'bold', color: '#5e5873', offsetY: 6, formatter: function (val) { return val + "%" } }
            }
          }
        },
        colors: [colorHex], stroke: { lineCap: 'round' }
      }
    },

    // Recursive Deep Search for Skills
    deepSearchSkill(obj, targetId) {
        if (!obj || typeof obj !== 'object') return null;
        if (obj.id === targetId && obj.comp) return obj;
        for (let key in obj) {
            let result = this.deepSearchSkill(obj[key], targetId);
            if (result) return result;
        }
        return null;
    },

    // Fetch from all JSONs with PCI Priority
    getSkillData(targetId) {
        if (!targetId) return null;
        let baseId = targetId;
        let parts = targetId.split("-");
        if (parts.length >= 3) baseId = `${parts[0]}-${parts[1]}`;

        const datasets = [
            (typeof pciDataCoach !== 'undefined' ? pciDataCoach : null),
            (typeof pciData !== 'undefined' ? pciData : null),
            (typeof deepData !== 'undefined' ? deepData : null),
            (typeof peopletreeData !== 'undefined' ? peopletreeData : null),
            (typeof baseData !== 'undefined' ? baseData : null),
            (this.$t ? this.$t("addNewSkill.data") : null) 
        ];

        for (let ds of datasets) {
            if (ds) {
                let found = this.deepSearchSkill(ds, baseId);
                if (found) return found;
            }
        }
        return null;
    },

    fetchFounders() {
      const coachId = this.$store.state.AppActiveUser.usersId;
      let query = coachId === 1 ? { query: { status: 'active' } } : { query: { coachId, status: 'active' } };
      
      this.$store.dispatch('traineeCoachAssignment/fetchTrainees', query)
        .then(res => {
          this.assignedFounders = this.$store.state.traineeCoachAssignment.traineeList;
          if(this.assignedFounders.length > 0) {
            this.selectFounder(this.assignedFounders[0]);
          }
        })
        .catch(err => console.error("Could not fetch trainees", err));
    },
    
    async selectFounder(founder) {
      this.selectedFounder = founder;
      const founderId = founder.userData[0].id;
      
      this.skillsMastered = 0;
      this.skillsWorkingOn = 0;
      this.skillsAwaitingReview = 0;
      this.currentProgress = 0;
      this.dynamicPendingEvidence = [];
      this.founderSkills = []; 
      this.selectedSkillId = null;
      this.skillPulseData = { relevancy: 0, done: 0, doing: 0 }; 

      try {
        const res = await this.$store.dispatch("trainee-competencies/find", { 
          query: { usersId: founderId } 
        });

        if (res && res.data) {
          let mastered = 0;
          let working = 0;
          let awaiting = 0;
          let evidenceList = [];
          
          let uniqueSkillsMap = new Map();

          for (let skill of res.data) {
            
            let compDetails = this.getSkillData(skill.competencyId);
            let actualSkillName = compDetails ? compDetails.comp : (skill.skill_name || skill.competencyId);

            try {
              let pmRes = await this.$store.dispatch("trainee-competencies-prgms/find", { 
                query: { trainee_competencies_id: skill.id } 
              });
              let pmTitles = [];
              if (pmRes && pmRes.data && compDetails && compDetails.progressMeasures) {
                pmRes.data.forEach(dbPm => {
                  let measureId = dbPm.progress_measure_id || dbPm.progressMeasureId || dbPm.prgms_id || dbPm.id;
                  let matchedPm = compDetails.progressMeasures.find(p => p.id === measureId);
                  if (matchedPm && matchedPm.title) pmTitles.push(matchedPm.title);
                });
              }
              if (pmTitles.length > 0) {
                actualSkillName = `${actualSkillName} (${pmTitles.join(', ')})`;
              }
            } catch(err) {}

            let startDate = new Date(skill.createdAt || skill.updatedAt || 0).getTime();

            // Base stats for fallback
            let baseRelevancy = skill.relevancy !== undefined && skill.relevancy !== null ? skill.relevancy : (skill.initialLevel ? parseInt(skill.initialLevel) * 10 : 0);
            let baseProgress = skill.progress || 0;
            let basePractice = skill.practice || 0;

            if (!uniqueSkillsMap.has(actualSkillName)) {
                uniqueSkillsMap.set(actualSkillName, { 
                    id: skill.id, 
                    skillName: actualSkillName,
                    startDate: startDate,
                    baseRelevancy: baseRelevancy,
                    baseProgress: baseProgress,
                    basePractice: basePractice
                });
            }

            if (skill.progressStatus === 'complete') {
                mastered++;
            } else if (skill.progressStatus === 'in_progress') {
                working++;
            } else if (skill.progressStatus === 'pending_review' || skill.progressStatus === 'pending' || skill.progressStatus === 'under_review') {
              awaiting++;
              evidenceList.push({
                id: skill.id,
                skillName: actualSkillName,
                date: this.formatDate(skill.updatedAt || skill.createdAt),
                type: "Document"
              });
            }
          }

          this.skillsMastered = mastered;
          this.skillsWorkingOn = working;
          this.skillsAwaitingReview = awaiting;
          this.dynamicPendingEvidence = evidenceList;

          let skillsArray = Array.from(uniqueSkillsMap.values());
          skillsArray.sort((a, b) => b.startDate - a.startDate);
          
          this.founderSkills = skillsArray;

          const total = mastered + working + awaiting;
          this.currentProgress = total > 0 ? Math.round((mastered / total) * 100) : 0;

          if(this.founderSkills.length > 0) {
            this.selectedSkillId = this.founderSkills[0].id; 
          }
        }

      } catch (err) {
        console.error("Failed to fetch founder metrics:", err);
      }
    },

    async fetchSkillProgress(traineeCompetenciesId) {
      try {
        const selectedSkillInfo = this.founderSkills.find(s => s.id === traineeCompetenciesId);
        let fallback = {
            relevancy: selectedSkillInfo ? selectedSkillInfo.baseRelevancy : 0,
            done: selectedSkillInfo ? selectedSkillInfo.baseProgress : 0,
            doing: selectedSkillInfo ? selectedSkillInfo.basePractice : 0
        };

        const res = await this.$store.dispatch('competency-progress/find', {
           query: { trainee_competencies_id: traineeCompetenciesId, $sort: { createdAt: -1 }, $limit: 1 }
        });

        if (res && res.data && res.data.length > 0) {
           const dbRecord = res.data[0];
           this.skillPulseData = {
             relevancy: (dbRecord.relevancy !== undefined && dbRecord.relevancy !== null) ? dbRecord.relevancy : fallback.relevancy,
             done: (dbRecord.progress !== undefined && dbRecord.progress !== null) ? dbRecord.progress : fallback.done,
             doing: (dbRecord.practice !== undefined && dbRecord.practice !== null) ? dbRecord.practice : fallback.doing
           };
        } else {
           this.skillPulseData = fallback;
        }

        // External API Fallback check (same as user side)
        if (this.skillPulseData.done === 0 && this.skillPulseData.doing === 0 && this.selectedFounder && this.selectedFounder.userData[0].email) {
            const email = this.selectedFounder.userData[0].email;
            const orgId = this.$store.state.activeCoachOrgId || "default_org";
            
            fetch(`https://parser.ai.getboarded.com/api/coach/progress?email=${encodeURIComponent(email)}&org_id=${encodeURIComponent(orgId)}`)
              .then(response => response.json())
              .then(apiRes => {
                  if (apiRes && apiRes.data && (apiRes.data.progress || apiRes.data.practice)) {
                      this.skillPulseData = {
                          relevancy: this.skillPulseData.relevancy, 
                          done: apiRes.data.progress || 0,
                          doing: apiRes.data.practice || 0
                      };
                  }
              }).catch(e => { /* Ignore API errors */ });
        }

      } catch (err) {
        const selectedSkillInfo = this.founderSkills.find(s => s.id === traineeCompetenciesId);
        this.skillPulseData = {
            relevancy: selectedSkillInfo ? selectedSkillInfo.baseRelevancy : 0,
            done: selectedSkillInfo ? selectedSkillInfo.baseProgress : 0,
            doing: selectedSkillInfo ? selectedSkillInfo.basePractice : 0
        };
      }
    },

    formatDate(dateStr) {
      if(!dateStr) return 'N/A';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    editData(id) {
      this.sidebarData = id;
      this.$store.dispatch("traineeCoachAssignment/setSidebarId", id).then(res => { this.toggleDataSidebar(true); });
    },
    toggleDataSidebar(val = false) {
      if (val === false) this.$store.dispatch("traineeCoachAssignment/setSidebarId", 0);
      this.addNewDataSidebar = val;
    },
    updateMonth(val) { this.showDate = this.$refs.calendar.getIncrementedPeriod(val); },
    scheduleMeeting(userId) {
      this.currentTraineeId = userId; 
      this.currentTraineeName = this.selectedFounder.userData[0].firstName;
      this.showDate = new Date(); 
      this.calenderPopup = true; 

      let eventData = [];
      const coachId = this.$store.state.AppActiveUser.usersId;

      this.$store.dispatch("notesManagement/fetchNotes", { 
        query: { $and: [{ authorId: { $in: [coachId, userId] } }, { noteType: 4 }, { status: 0 }] } 
      }).then(res => {
        if (res && res.data) {
          res.data.forEach(meeting => {
            if (meeting.meetingDate) {
              eventData.push({ title: "Meeting: " + this.currentTraineeName, startDate: meeting.meetingDate, endDate: meeting.meetingDate, classes: "event-warning" });
            }
          });
        }
        this.eventsData = [...new Set(eventData)];
      });
    },

    promptAddMeeting(date) {
      const clickedDate = new Date(date);
      if (clickedDate < new Date().setHours(0,0,0,0)) return this.$vs.notify({ title: "Invalid Date", text: "Cannot schedule a past meeting.", color: "warning" });
      this.meetingUrl = ''; this.msg = ''; this.meetingDate = clickedDate; 
      const now = new Date(); this.startH = now.getHours(); this.startM = (now.getMinutes() + 5) % 60;
      this.activePromptAddMeeting = true; 
    },
    
    async createMeeting() {
      const d = new Date(this.meetingDate);
      const startD = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const schedule_time = `${startD} ${String(this.startH).padStart(2, '0')}:${String(this.startM).padStart(2, '0')}:00`;
      let finalMeetingUrl = this.useMyMeetingLink ? this.myMeetingLink : this.meetingUrl;
      let finalMessage = this.msg + (finalMeetingUrl ? `<br><br>📍 <strong>Meeting Link:</strong> ${finalMeetingUrl}` : '');

      const note = {
        userRole: this.$store.state.AppActiveUser.userType, noteType: 4, authorId: this.$store.state.AppActiveUser.usersId,
        commuId: this.currentTraineeId, company: window.$cookies.get('companyName'), message: finalMessage, timezone: this.timezone,
        status: 0, meetingDate: schedule_time
      };

      try {
        await this.$store.dispatch("notesManagement/addNote", note);
        this.$vs.notify({ title: "Success", text: "Meeting Scheduled", color: "success" });
        this.eventsData.push({ title: `Meeting scheduled`, startDate: schedule_time, endDate: schedule_time, classes: "event-warning" });
        this.activePromptAddMeeting = false;
      } catch (err) {
        this.$vs.notify({ title: "Failed", text: "Failed to schedule", color: "danger" });
      }
    },
    triggerNotes() {
      setTimeout(() => {
        const comp = Array.isArray(this.$refs.coachNotesRef) ? this.$refs.coachNotesRef[0] : this.$refs.coachNotesRef;
        if (!comp) return;
        const possibleVars = ['active', 'isSidebarActive', 'sidebarActive', 'activePrompt', 'showNotes', 'popupActive', 'isNotesActive'];
        possibleVars.forEach(v => { if (v in comp) comp[v] = true; });
        const possibleMethods = ['open', 'show', 'toggle', 'openNotes', 'showNotes', 'toggleNotes'];
        possibleMethods.forEach(m => { if (typeof comp[m] === 'function') comp[m](); });

        this.$nextTick(() => {
          const el = comp.$el;
          if (!el) return;
          const fireClick = (target) => {
            if (!target) return;
            if (typeof target.click === 'function') target.click(); 
            const ev = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
            target.dispatchEvent(ev); 
          };
          fireClick(el); 
          const firstChild = el.firstElementChild;
          if (firstChild) fireClick(firstChild); 
          const svg = el.querySelector('svg');
          if (svg) fireClick(svg); 
        });
      }, 50); 
    },
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.4s ease; }
.fade-enter, .fade-leave-to { opacity: 0; transform: translateY(10px); }
.bg-primary-light { background-color: rgba(115, 103, 240, 0.12); }
.meeting-popup .vs-popup { z-index: 9999999 !important; }
.custom-calendar-view .cv-day { cursor: pointer; }
.custom-calendar-view .cv-day:hover { background-color: rgba(0, 0, 0, 0.03); }
</style>

<style lang="scss">
@import "@/assets/scss/vuexy/apps/simple-calendar.scss";
.custom-calendar-popup { z-index: 99999 !important; }
.custom-calendar-popup .vs-popup { width: 90vw !important; max-width: 1200px !important; border-radius: 12px !important; }
.custom-calendar-popup .vs-popup--header { z-index: 100000 !important; position: relative; }
.custom-calendar-popup .vs-popup--content { padding: 10px 20px 20px !important; height: auto !important; max-height: 80vh !important; overflow-y: auto !important; }
.custom-calendar-popup .custom-calendar-view { min-height: 65vh !important; }
</style>